package com.presentocracy.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.presentocracy.shared.model.Birthday
import com.presentocracy.shared.model.BaseVisibility
import com.presentocracy.shared.model.Gift
import com.presentocracy.shared.model.Friendship
import com.presentocracy.shared.model.FriendshipStatus
import com.presentocracy.shared.model.UserCard
import com.presentocracy.shared.model.Wishlist
import com.presentocracy.shared.model.ReservationStatus
import com.presentocracy.shared.model.ReservationVisibility
import com.presentocracy.shared.validation.UserInputRules
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Aside
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.renderComposable
import kotlin.js.Date

fun main() {
    renderComposable(rootElementId = "root") {
        val controller = remember { WebController() }
        val reviewMode = remember { isDesignReviewRequested() }
        DisposableEffect(controller) {
            if (!reviewMode) controller.start()
            onDispose { controller.close() }
        }
        if (reviewMode) DesignReview() else PresentocracyApp(controller)
    }
}

@Composable
private fun PresentocracyApp(controller: WebController) {
    if (controller.linkedWishlistId != null) {
        if (controller.state == WebAppState.Loading || controller.linkedWishlistLoading) {
            SplashScreen()
            return
        }
        val wishlist = controller.linkedWishlist
        Div(attrs = { classes("app-shell") }) {
            Section(attrs = { classes("panel") }) {
                SecondaryButton("Назад", controller::closeWishlistLink)
                if (wishlist != null) {
                    H1 { Text(wishlist.name) }
                    wishlist.description?.let { P { Text(it) } }
                    if (controller.state is WebAppState.SignedIn) {
                        ExternalWishlistCard(wishlist, controller)
                    } else {
                        wishlist.gifts.forEach { gift ->
                            Div(attrs = { classes("gift-card") }) {
                                H3 { Text(gift.title) }
                                gift.description?.let { P { Text(it) } }
                                val amount = gift.priceMinorUnits
                                val currency = gift.priceCurrency
                                if (amount != null && currency != null) P { Text(formatMinorUnits(amount, currency)) }
                            }
                        }
                    }
                } else {
                    H2 { Text("Вишлист не найден или недоступен") }
                    P { Text("Ссылка не меняет приватность. Войдите в аккаунт, которому владелец предоставил доступ.") }
                    SecondaryButton("Повторить", controller::retryWishlistLink)
                }
            }
        }
        if (wishlist == null && controller.state == WebAppState.SignedOut) AuthScreen(controller)
        return
    }
    when (val state = controller.state) {
        WebAppState.Loading -> SplashScreen()
        WebAppState.SignedOut -> AuthScreen(controller)
        is WebAppState.SignedIn -> MainShell(controller, state)
    }
}

@Composable
private fun AuthScreen(controller: WebController) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var username by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf("") }
    val register = controller.authMode == WebAuthMode.REGISTER
    val verificationEmail = controller.verificationEmail

    Div(attrs = { classes("auth-page", "auth-split") }) {
        Section(attrs = { classes("auth-story") }) {
            Div(attrs = { classes("auth-wordmark") }) { Text("presentocracy") }
            Div(attrs = { classes("auth-story-copy") }) {
                Span(attrs = { classes("eyebrow") }) { Text("ПОДАРКИ СТАНОВЯТСЯ БЛИЖЕ") }
                H1 { Text("Желания, которыми легко делиться.") }
                P { Text("Собирайте идеи, открывайте списки близким и сохраняйте сюрприз до нужного момента.") }
            }
            Div(attrs = { classes("auth-story-note") }) {
                Span { Text("✦") }
                P { Text("Каждый подарок можно бронировать вместе — создатель списка увидит только то, что разрешил сам.") }
            }
        }
        Div(attrs = { classes("auth-form-wrap") }) {
        if (verificationEmail != null) {
            EmailVerificationForm(controller, verificationEmail)
        } else Div(attrs = { classes("auth-card") }) {
            Div(attrs = { classes("auth-mobile-brand") }) { Text("presentocracy") }
            Span(attrs = { classes("eyebrow") }) { Text(if (register) "НОВЫЙ АККАУНТ" else "С ВОЗВРАЩЕНИЕМ") }
            H1 { Text(if (register) "Создайте своё пространство." else "Войдите в аккаунт.") }
            P(attrs = { classes("muted") }) { Text(if (register) "Начните собирать идеи для себя и друзей." else "Ваши списки и важные даты уже ждут вас.") }
            if (register) {
                TextField(
                    username,
                    { username = it },
                    "Username",
                    placeholder = "например, alex",
                    autocomplete = "username",
                    name = "username",
                )
                TextField(displayName, { displayName = it }, "Имя", placeholder = "Как к вам обращаться")
            }
            TextField(
                email,
                { email = it },
                "Email",
                InputType.Email,
                "you@example.com",
                autocomplete = "email",
                name = "email",
            )
            TextField(
                password,
                { password = it },
                "Пароль",
                InputType.Password,
                if (register) "Минимум 8 символов, буква и цифра" else "Ваш пароль",
                autocomplete = if (register) "new-password" else "current-password",
                name = "password",
            )
            controller.message?.let { MessageBanner(it, error = true) }
            PrimaryButton(
                if (register) "Создать аккаунт" else "Войти",
                onClick = { controller.submitAuth(email, password, username, displayName) },
                disabled = controller.isBusy || email.isBlank() || password.isBlank() ||
                    (register && (username.isBlank() || displayName.isBlank() || UserInputRules.validatePassword(password) != null)),
                loading = controller.isBusy,
            )
            SecondaryButton(
                controller.resendCaption(email),
                onClick = { controller.resendVerification(email) },
                disabled = controller.isBusy || email.isBlank() || controller.resendWaitSeconds(email) > 0,
            )
            Div(attrs = { classes("auth-switch") }) {
                Span { Text(if (register) "Уже зарегистрированы?" else "Ещё нет аккаунта?") }
                Button(attrs = { classes("text-button"); onClick {
                    controller.authMode = if (register) WebAuthMode.LOGIN else WebAuthMode.REGISTER
                    controller.clearMessage()
                } }) { Text(if (register) "Войти" else "Создать аккаунт") }
            }
            Div(attrs = { classes("auth-divider") }) {
                Span()
                Text("или")
                Span()
            }
            GoogleSignInButton(
                onCredential = controller::signInWithGoogle,
                disabled = controller.isBusy,
            )
            Div(attrs = { classes("auth-privacy") }) { Text("Ваши личные списки видны только тем, кого вы выбрали.") }
        }
        }
    }
}

@Composable
private fun EmailVerificationForm(controller: WebController, email: String) {
    var code by remember(email) { mutableStateOf("") }
    Div(attrs = { classes("auth-card") }) {
        Div(attrs = { classes("auth-mobile-brand") }) { Text("presentocracy") }
        Span(attrs = { classes("eyebrow") }) { Text("ПОДТВЕРЖДЕНИЕ EMAIL") }
        H1 { Text("Проверьте почту.") }
        P(attrs = { classes("muted") }) { Text("Введите 6-значный код, отправленный на $email.") }
        TextField(
            code,
            { code = it.filter(Char::isDigit).take(6) },
            "Код подтверждения",
            placeholder = "123456",
            autocomplete = "one-time-code",
            name = "verification_code",
            maxlength = 6,
        )
        controller.message?.let { MessageBanner(it, error = true) }
        PrimaryButton(
            "Подтвердить email",
            onClick = { controller.verifyEmailCode(code) },
            disabled = controller.isBusy || code.length != 6,
            loading = controller.isBusy,
        )
        SecondaryButton(
            controller.resendCaption(email),
            onClick = { controller.resendVerification(email) },
            disabled = controller.isBusy || controller.resendWaitSeconds(email) > 0,
        )
        Button(attrs = {
            classes("text-button")
            onClick { controller.cancelEmailVerification() }
        }) { Text("Указать другой email") }
    }
}

private enum class ReviewVariant(val title: String, val subtitle: String) {
    EDITORIAL("A · Editorial", "Спокойный журнал: крупная типографика, мягкие карточки и один главный акцент."),
    CALM("B · Calm cards", "Фокус на сканировании: короткие блоки, статусы и быстрые действия рядом с контентом."),
    CELEBRATION("C · Celebration", "Более эмоциональный вариант: цвет, дата и повод становятся частью первого экрана."),
}

private enum class ReviewScreen(val label: String, val icon: String) {
    HOME("Главная", "⌂"),
    WISHLISTS("Списки", "▤"),
    FRIENDS("Друзья", "♧"),
    BIRTHDAYS("Даты", "✦"),
    PROFILE("Профиль", "●"),
}

@Composable
private fun DesignReview() {
    var variant by remember { mutableStateOf(ReviewVariant.EDITORIAL) }
    var screen by remember { mutableStateOf(ReviewScreen.HOME) }

    Div(attrs = { classes("design-review") }) {
        Div(attrs = { classes("review-header") }) {
            Div {
                Span(attrs = { classes("eyebrow") }) { Text("PRESENTOCRACY / DESIGN REVIEW") }
                H1 { Text("Три спокойных направления для мобильного приложения") }
                P(attrs = { classes("muted") }) {
                    Text("Переключайте стиль и экран, чтобы быстро сравнить плотность, настроение и приоритеты интерфейса.")
                }
            }
            A(href = "?", attrs = { classes("review-exit") }) { Text("Вернуться в приложение →") }
        }
        Div(attrs = { classes("review-variants") }) {
            ReviewVariant.values().forEach { option ->
                Button(attrs = {
                    classes("review-variant")
                    if (variant == option) classes("review-variant-selected")
                    attr("aria-pressed", (variant == option).toString())
                    onClick { variant = option }
                }) {
                    Span(attrs = { classes("review-variant-title") }) { Text(option.title) }
                    Span(attrs = { classes("review-variant-subtitle") }) { Text(option.subtitle) }
                }
            }
        }
        Div(attrs = { classes("review-workspace") }) {
            Div(attrs = { classes("phone-stage") }) {
                Div(attrs = { classes("phone-frame", "review-${variant.name.lowercase()}") }) {
                    ReviewPhoneHeader(variant)
                    Div(attrs = { classes("phone-content") }) {
                        when (screen) {
                            ReviewScreen.HOME -> ReviewHome(variant)
                            ReviewScreen.WISHLISTS -> ReviewWishlists(variant)
                            ReviewScreen.FRIENDS -> ReviewFriends(variant)
                            ReviewScreen.BIRTHDAYS -> ReviewBirthdays(variant)
                            ReviewScreen.PROFILE -> ReviewProfile(variant)
                        }
                    }
                    Div(attrs = { classes("phone-nav") }) {
                        ReviewScreen.values().forEach { option ->
                            Button(attrs = {
                                classes("phone-nav-item")
                                if (screen == option) classes("phone-nav-item-selected")
                                attr("aria-label", option.label)
                                attr("aria-pressed", (screen == option).toString())
                                onClick { screen = option }
                            }) {
                                Span(attrs = { classes("phone-nav-icon") }) { Text(option.icon) }
                                Span { Text(option.label) }
                            }
                        }
                    }
                }
            }
            ReviewNotes(variant, screen)
        }
    }
}

@Composable
private fun ReviewPhoneHeader(variant: ReviewVariant) {
    Div(attrs = { classes("phone-status") }) {
        Span { Text("11:30") }
        Span(attrs = { classes("phone-status-icons") }) { Text("•••  ▰") }
    }
    Div(attrs = { classes("phone-topbar") }) {
        Div {
            Span(attrs = { classes("phone-kicker") }) {
                Text(if (variant == ReviewVariant.CELEBRATION) "TODAY, 25 AUGUST" else "YOUR GIFT SPACE")
            }
            H2 { Text("Presentocracy") }
        }
        Span(attrs = { classes("phone-avatar") }) { Text("D") }
    }
}

@Composable
private fun ReviewHome(variant: ReviewVariant) {
    when (variant) {
        ReviewVariant.EDITORIAL -> {
            Div(attrs = { classes("review-hero-card") }) {
                Span(attrs = { classes("review-card-label") }) { Text("ВАШЕ ПРОСТРАНСТВО ПОДАРКОВ") }
                H3 { Text("Желания, которыми легко делиться.") }
                P { Text("Создавайте списки, добавляйте идеи и отправляйте близким одну понятную ссылку.") }
                Button(attrs = { classes("review-primary-action") }) { Text("Создать список") }
            }
            Div(attrs = { classes("review-stat-row") }) {
                ReviewStat("3", "списка")
                ReviewStat("12", "идей")
                ReviewStat("5", "друзей")
            }
            ReviewBirthdayCard("Ближайший день рождения", "Анна", "через 7 дней")
        }
        ReviewVariant.CALM -> {
            Div(attrs = { classes("review-heading-row") }) {
                Div {
                    Span(attrs = { classes("review-card-label") }) { Text("СЕГОДНЯ") }
                    H3 { Text("Что важно не забыть") }
                }
                Span(attrs = { classes("review-count") }) { Text("3 события") }
            }
            ReviewActionRow("✦", "Список подарков", "5 идей · открыт друзьям", "Открыть")
            ReviewActionRow("◷", "День рождения Анны", "через 7 дней", "Посмотреть")
            ReviewActionRow("+", "Новая идея", "Добавить в любой список", "Добавить")
        }
        ReviewVariant.CELEBRATION -> {
            Div(attrs = { classes("review-celebration-card") }) {
                Span(attrs = { classes("review-card-label") }) { Text("СОБЫТИЕ РЯДОМ") }
                H3 { Text("Подарок для Анны") }
                P { Text("День рождения уже через 7 дней — откройте её wishlist и выберите то, что точно порадует.") }
                Button(attrs = { classes("review-primary-action") }) { Text("Открыть список") }
            }
            Div(attrs = { classes("review-date-strip") }) {
                Span { Text("25") }
                Div {
                    StrongText("августа")
                    P { Text("Сегодня хороший день для идеи") }
                }
            }
            ReviewQuote("Хороший подарок начинается с внимания.")
        }
    }
}

@Composable
private fun ReviewWishlists(variant: ReviewVariant) {
    ReviewScreenHeading("Ваши списки", "Соберите идеи по поводам и поделитесь ими, когда будете готовы.")
    if (variant == ReviewVariant.CELEBRATION) {
        ReviewListCard("День рождения 2026", "5 идей", "открыт друзьям", "🎁")
        ReviewListCard("Для дома", "3 идеи", "только я", "⌂")
    } else {
        ReviewListCard("День рождения 2026", "5 идей", "открыт друзьям", "✦")
        ReviewListCard("Для дома", "3 идеи", "только я", "⌂")
        ReviewListCard("Путешествие", "4 идеи", "выбранные люди", "↗")
    }
    Button(attrs = { classes("review-fab") }) { Text("+") }
}

@Composable
private fun ReviewFriends(variant: ReviewVariant) {
    ReviewScreenHeading("Друзья", "Список людей, с которыми можно делиться желаниями без лишних настроек.")
    Div(attrs = { classes("review-search") }) { Text("⌕  Найти по имени или username") }
    listOf("Анна", "Миша", "Катя").forEachIndexed { index, name ->
        ReviewPersonRow(name, "@${name.lowercase()}", if (index == 2) "Запрос" else "Друг", variant)
    }
}

@Composable
private fun ReviewBirthdays(variant: ReviewVariant) {
    ReviewScreenHeading("Дни рождения", "Важные даты друзей — в порядке ближайшего повода.")
    ReviewBirthdayCard("СЛЕДУЮЩИЙ ПОВОД", "Анна", "1 сентября · через 7 дней")
    ReviewBirthdayCard("ПОТОМ", "Миша", "18 сентября · через 24 дня")
    if (variant == ReviewVariant.CELEBRATION) ReviewQuote("Сохраните дату — идея найдётся вовремя.")
}

@Composable
private fun ReviewProfile(variant: ReviewVariant) {
    ReviewScreenHeading("Профиль", "Настройте то, как вас видят друзья.")
    Div(attrs = { classes("review-profile-card") }) {
        Span(attrs = { classes("review-profile-avatar") }) { Text("D") }
        Div {
            H3 { Text("Denis") }
            P { Text("@dast3r") }
        }
        Span(attrs = { classes("review-edit-mark") }) { Text("✎") }
    }
    ReviewField("Имя", "Denis")
    ReviewField("О себе", if (variant == ReviewVariant.CELEBRATION) "Люблю выбирать подарки с историей" else "рост 170")
    ReviewField("Валюта", "EUR")
    Button(attrs = { classes("review-primary-action", "review-wide-action") }) { Text("Сохранить изменения") }
}

@Composable
private fun ReviewScreenHeading(title: String, description: String) {
    Span(attrs = { classes("review-card-label") }) { Text("PRESENTOCRACY") }
    H3 { Text(title) }
    P(attrs = { classes("review-screen-description") }) { Text(description) }
}

@Composable
private fun ReviewStat(value: String, label: String) {
    Div(attrs = { classes("review-stat") }) {
        Span { Text(value) }
        P { Text(label) }
    }
}

@Composable
private fun ReviewBirthdayCard(label: String, name: String, date: String) {
    Div(attrs = { classes("review-birthday") }) {
        Span(attrs = { classes("review-birthday-icon") }) { Text("✦") }
        Div {
            Span(attrs = { classes("review-card-label") }) { Text(label) }
            H3 { Text(name) }
            P { Text(date) }
        }
    }
}

@Composable
private fun ReviewActionRow(icon: String, title: String, detail: String, action: String) {
    Div(attrs = { classes("review-action-row") }) {
        Span(attrs = { classes("review-action-icon") }) { Text(icon) }
        Div {
            H3 { Text(title) }
            P { Text(detail) }
        }
        Span(attrs = { classes("review-row-action") }) { Text(action) }
    }
}

@Composable
private fun ReviewListCard(title: String, count: String, visibility: String, icon: String) {
    Div(attrs = { classes("review-list-card") }) {
        Span(attrs = { classes("review-list-icon") }) { Text(icon) }
        Div {
            H3 { Text(title) }
            P { Text("$count · $visibility") }
        }
        Span(attrs = { classes("review-row-action") }) { Text("→") }
    }
}

@Composable
private fun ReviewPersonRow(name: String, username: String, status: String, variant: ReviewVariant) {
    Div(attrs = { classes("review-person-row") }) {
        Span(attrs = {
            classes("review-person-avatar")
            if (variant == ReviewVariant.CELEBRATION) classes("person-avatar-warm")
        }) {
            Text(name.take(1))
        }
        Div {
            H3 { Text(name) }
            P { Text(username) }
        }
        Span(attrs = { classes("review-status", if (status == "Друг") "review-status-ok" else "review-status-pending") }) {
            Text(status)
        }
    }
}

@Composable
private fun ReviewField(label: String, value: String) {
    Div(attrs = { classes("review-field") }) {
        Span { Text(label) }
        P { Text(value) }
    }
}

@Composable
private fun ReviewQuote(text: String) {
    Div(attrs = { classes("review-quote") }) { Text("“$text”") }
}

@Composable
private fun ReviewNotes(variant: ReviewVariant, screen: ReviewScreen) {
    Div(attrs = { classes("review-notes") }) {
        Span(attrs = { classes("eyebrow") }) { Text("${variant.title} / ${screen.label}") }
        H2 { Text("Что проверяем") }
        P(attrs = { classes("muted") }) { Text(variant.subtitle) }
        Div(attrs = { classes("review-note-list") }) {
            ReviewNote("Иерархия", when (variant) {
                ReviewVariant.EDITORIAL -> "Один крупный повод и один CTA на первом экране."
                ReviewVariant.CALM -> "Все ключевые действия видны без прокрутки."
                ReviewVariant.CELEBRATION -> "Дата и социальный контекст сразу объясняют, зачем открыть приложение."
            })
            ReviewNote("Плотность", when (variant) {
                ReviewVariant.EDITORIAL -> "Больше воздуха, меньше визуального шума."
                ReviewVariant.CALM -> "Компактнее и быстрее сканируется большим списком."
                ReviewVariant.CELEBRATION -> "Самый эмоциональный, но требует аккуратного контента."
            })
            ReviewNote("Рекомендация", when (variant) {
                ReviewVariant.EDITORIAL -> "Базовый кандидат для основного продукта."
                ReviewVariant.CALM -> "Хорош для power users и частого возврата."
                ReviewVariant.CELEBRATION -> "Подходит как сезонная тема или экран события."
            })
        }
        Div(attrs = { classes("review-checklist") }) {
            Span(attrs = { classes("review-card-label") }) { Text("Быстрый чек-лист") }
            P { Text("Сравните первый взгляд, понятность CTA, ощущение приватности и то, насколько легко найти ближайший день рождения.") }
        }
    }
}

@Composable
private fun ReviewNote(title: String, description: String) {
    Div(attrs = { classes("review-note") }) {
        StrongText(title)
        P { Text(description) }
    }
}

@Composable
private fun MainShell(controller: WebController, state: WebAppState.SignedIn) {
    Div(attrs = { classes("app-shell") }) {
        Aside(attrs = { classes("sidebar") }) {
            Div(attrs = { classes("sidebar-brand") }) { Text("presentocracy") }
            Nav(attrs = { classes("sidebar-nav") }) {
                NavButton("Главная", WebTab.HOME, controller)
                NavButton("Вишлисты", WebTab.WISHLISTS, controller)
                NavButton("Друзья", WebTab.FRIENDS, controller)
                NavButton("Календарь", WebTab.BIRTHDAYS, controller)
                NavButton("Профиль", WebTab.PROFILE, controller)
                NavButton("Настройки", WebTab.SETTINGS, controller)
            }
            Div(attrs = { classes("sidebar-user") }) {
                Span(attrs = { classes("avatar", "sidebar-avatar") }) { Text(state.user.displayName.take(1).uppercase()) }
                Div {
                    StrongText(state.user.displayName)
                    Span(attrs = { classes("sidebar-handle") }) { Text("@${state.user.username}") }
                }
            }
        }
        Div(attrs = { classes("mobile-header") }) {
            Div(attrs = { classes("mobile-brand") }) {
                Span(attrs = { classes("mobile-brand-mark") }) { Text("P") }
                Div {
                    StrongText("Presentocracy")
                    Span(attrs = { classes("mobile-greeting") }) { Text("Пространство подарков") }
                }
            }
            Div(attrs = { classes("mobile-header-actions") }) {
                Span(attrs = { classes("mobile-user-name") }) { Text(state.user.displayName) }
                SecondaryButton("Выйти", controller::logout, disabled = controller.isBusy)
            }
        }
        Main(attrs = { classes("content") }) {
            controller.message?.let { MessageBanner(it, error = false) }
            when (controller.selectedTab) {
                WebTab.HOME -> HomeView(state, controller)
                WebTab.WISHLISTS -> WishlistsView(state, controller)
                WebTab.FRIENDS -> FriendsView(state, controller)
                WebTab.BIRTHDAYS -> BirthdaysView(state)
                WebTab.PROFILE -> ProfileView(state, controller)
                WebTab.SETTINGS -> SettingsView(state, controller)
            }
        }
        Nav(attrs = { classes("bottom-nav") }) {
            NavButton("Главная", WebTab.HOME, controller)
            NavButton("Списки", WebTab.WISHLISTS, controller)
            NavButton("Друзья", WebTab.FRIENDS, controller)
            NavButton("Даты", WebTab.BIRTHDAYS, controller)
            NavButton("Профиль", WebTab.PROFILE, controller)
        }
        if (controller.selectedTab == WebTab.WISHLISTS && !controller.wishlistDetailOpen) {
            Button(attrs = { classes("fab"); attr("aria-label", "Новый вишлист"); onClick { controller.showCreateWishlist = true } }) { Text("+") }
        }
    }
}

@Composable
private fun NavButton(label: String, tab: WebTab, controller: WebController) {
    Button(attrs = {
        classes("nav-button")
        if (controller.selectedTab == tab) classes("nav-button-active")
        attr("aria-label", label)
        attr("aria-current", if (controller.selectedTab == tab) "page" else "false")
        onClick {
            controller.closeWishlistDetails()
            controller.selectedTab = tab
        }
    }) {
        Span(attrs = { classes("nav-icon") }) { Text(navIcon(tab)) }
        Span(attrs = { classes("nav-label") }) { Text(label) }
    }
}

private fun navIcon(tab: WebTab): String = when (tab) {
    WebTab.HOME -> "⌂"
    WebTab.WISHLISTS -> "▤"
    WebTab.FRIENDS -> "♧"
    WebTab.BIRTHDAYS -> "✦"
    WebTab.PROFILE -> "●"
    WebTab.SETTINGS -> "⚙"
}

@Composable
private fun SplashScreen() {
    Div(attrs = { classes("splash-screen") }) {
        Div(attrs = { classes("splash-icon") }) { Text("P") }
    }
}

@Composable
private fun HomeView(state: WebAppState.SignedIn, controller: WebController) {
    Div(attrs = { classes("home-intro") }) {
        Span(attrs = { classes("home-greeting") }) { Text("Доброе утро, ${state.user.displayName.substringBefore(' ')}") }
        H1(attrs = { classes("home-title") }) { Text("Чем хочется порадовать себя?") }
    }
    Section(attrs = { classes("hero") }) {
        Div {
            Span(attrs = { classes("eyebrow") }) { Text("ВАШЕ ПРОСТРАНСТВО ПОДАРКОВ") }
            H1 { Text("Сохраните идею, пока она не потерялась.") }
            P(attrs = { classes("hero-copy") }) { Text("Добавьте ссылку, фотографию или просто название будущего подарка.") }
            Div(attrs = { classes("button-row") }) {
                PrimaryButton("＋ Добавить желание", onClick = {
                    controller.closeWishlistDetails()
                    controller.selectedTab = WebTab.WISHLISTS
                    val last = state.wishlists.lastOrNull()
                    if (last == null) controller.showCreateWishlist = true
                    else {
                        controller.openWishlistDetails(last.id)
                        controller.showCreateGift = true
                    }
                })
            }
        }
        Div(attrs = { classes("hero-orbit") }) {
            Span(attrs = { classes("orbit-dot", "orbit-dot-one") })
            Span(attrs = { classes("orbit-dot", "orbit-dot-two") })
            Span(attrs = { classes("orbit-dot", "orbit-dot-three") })
            Span(attrs = { classes("orbit-center") }) { Text("✦") }
        }
    }
    Div(attrs = { classes("stats-grid") }) {
        StatCard(state.wishlists.size.toString().padStart(2, '0'), "вишлиста", "")
        StatCard(state.wishlists.sumOf { it.gifts.size }.toString().padStart(2, '0'), "желаний", "")
        StatCard(state.friends.count { it.status == FriendshipStatus.ACCEPTED }.toString().padStart(2, '0'), "друзей", "")
    }
    Div(attrs = { classes("home-lower") }) {
        Section(attrs = { classes("home-list") }) {
            H2(attrs = { classes("home-list-title") }) { Text("Ближайшие дни рождения") }
            if (state.birthdays.isEmpty()) EmptyState("Пока нет ближайших дат", "Добавьте друзей, чтобы видеть их дни рождения.")
            else state.birthdays.take(3).forEach { birthday ->
                Div(attrs = { classes("home-birthday-row") }) {
                    Span(attrs = { classes("birthday-short-date") }) { Text(formatBirthday(birthday.birthday)) }
                    Div {
                        StrongText(birthday.user.displayName)
                        P(attrs = { classes("muted") }) { Text("через ${birthday.daysUntil} дн.") }
                    }
                    Span(attrs = { classes("row-arrow") }) { Text("›") }
                }
            }
        }
        val recent = state.wishlists.lastOrNull()
        Section(attrs = { classes("recent-wishlist") }) {
            Span(attrs = { classes("eyebrow", "muted") }) { Text("ПОСЛЕДНИЙ ВИШЛИСТ") }
            if (recent == null) EmptyState("Пока нет вишлистов", "Создайте первый список желаний.")
            else {
                H2 { Text(recent.name) }
                P(attrs = { classes("muted") }) { Text("${recent.gifts.size} ${giftWord(recent.gifts.size)} · ${visibilityLabel(recent.baseVisibility)}") }
                recent.gifts.take(3).forEach { gift ->
                    Div(attrs = { classes("recent-gift") }) {
                        Span(attrs = { classes("recent-gift-icon") }) { Text("✦") }
                        Span { Text(gift.title) }
                    }
                }
                Button(attrs = { classes("text-button"); onClick { controller.selectedTab = WebTab.WISHLISTS; controller.openWishlistDetails(recent.id) } }) { Text("Открыть список →") }
            }
        }
    }
}

@Composable
private fun StatCard(number: String, label: String, detail: String) {
    Div(attrs = { classes("stat-card") }) {
        Span(attrs = { classes("stat-number") }) { Text(number) }
        StrongText(label)
        P(attrs = { classes("muted") }) { Text(detail) }
    }
}

@Composable
private fun WishlistsView(state: WebAppState.SignedIn, controller: WebController) {
    if (controller.wishlistDetailOpen) {
        val selected = state.wishlists.firstOrNull { it.id == controller.selectedWishlistId }
        if (selected == null) {
            LaunchedEffect(controller.selectedWishlistId) { controller.closeWishlistDetails() }
        } else {
            WishlistDetails(selected, controller)
        }
        return
    }

    var query by remember { mutableStateOf("") }
    val normalizedQuery = query.trim().lowercase()
    val filteredWishlists = state.wishlists
        .filter { wishlist ->
            normalizedQuery.isBlank() || listOf(wishlist.name, wishlist.slug, wishlist.description.orEmpty())
                .any { it.lowercase().contains(normalizedQuery) }
        }
    val totalGifts = state.wishlists.sumOf { it.gifts.size }
    val sharedWishlists = state.wishlists.count { it.baseVisibility != BaseVisibility.ONLY_ME }

    Div(attrs = { classes("page-heading") }) {
        Div {
            Span(attrs = { classes("eyebrow") }) { Text("YOUR GIFT SPACE") }
            H1 { Text("Ваши списки") }
            P(attrs = { classes("muted") }) { Text("Все идеи для подарков — собраны по случаям и всегда под рукой.") }
        }
        Div(attrs = { classes("page-heading-actions") }) {
            Span(attrs = { classes("heading-note") }) { Text("${state.wishlists.size} ${wishlistWord(state.wishlists.size)}") }
            PrimaryButton("+ Новый список", onClick = { controller.showCreateWishlist = true })
        }
    }

    Section(attrs = { classes("wishlist-overview") }) {
        Div(attrs = { classes("wishlist-overview-copy") }) {
            Span(attrs = { classes("eyebrow", "eyebrow-light") }) { Text("YOUR COLLECTION") }
            H2 { Text("Сохраняйте то, что хочется не забыть") }
            P { Text("Сделайте отдельный список для дома, путешествий или ближайшего праздника — и поделитесь им, когда будете готовы.") }
            Div(attrs = { classes("wishlist-overview-actions") }) {
                Button(attrs = { classes("overview-link"); onClick { controller.showCreateWishlist = true } }) {
                    Text(if (state.wishlists.isEmpty()) "Создать первый список  →" else "Создать новый список  →")
                }
                Span(attrs = { classes("overview-hint") }) { Text("Приватность можно изменить позже") }
            }
        }
        Div(attrs = { classes("overview-metrics") }) {
            OverviewMetric(totalGifts.toString(), "идей сохранено")
            OverviewMetric(sharedWishlists.toString(), "списка с доступом")
        }
    }

    if (controller.showCreateWishlist) CreateWishlistForm(controller)
    if (state.wishlists.isEmpty()) {
        Section(attrs = { classes("panel", "empty-panel") }) {
            EmptyState("Начните с первого списка", "Например, birthday-2026, home или travel.")
            PrimaryButton("Создать wishlist", onClick = { controller.showCreateWishlist = true })
        }
        return
    }

    Section(attrs = { classes("wishlist-browser") }) {
        Div(attrs = { classes("wishlist-toolbar") }) {
            Div(attrs = { classes("wishlist-search") }) {
                Span(attrs = { classes("search-icon") }) { Text("⌕") }
                Input(type = InputType.Text, attrs = {
                    classes("input")
                    value(query)
                    placeholder("Поиск по спискам")
                    attr("aria-label", "Поиск по спискам")
                    onInput { query = it.value }
                })
                if (query.isNotBlank()) {
                    Button(attrs = {
                        classes("search-clear")
                        attr("aria-label", "Очистить поиск")
                        onClick { query = "" }
                    }) { Text("×") }
                }
            }
            Span(attrs = { classes("result-count") }) {
                Text("${filteredWishlists.size} из ${state.wishlists.size}")
            }
        }
        if (filteredWishlists.isEmpty()) {
            EmptyState("Ничего не нашли", "Попробуйте изменить запрос или очистить поиск.")
        } else {
            Div(attrs = { classes("wishlist-grid") }) {
                filteredWishlists.forEach { wishlist ->
                    WishlistCard(wishlist, controller.selectedWishlistId == wishlist.id) {
                        controller.openWishlistDetails(wishlist.id)
                    }
                }
            }
        }
    }
}

@Composable
private fun OverviewMetric(value: String, label: String) {
    Div(attrs = { classes("overview-metric") }) {
        Span(attrs = { classes("overview-metric-value") }) { Text(value) }
        Span(attrs = { classes("overview-metric-label") }) { Text(label) }
    }
}

@Composable
private fun WishlistCard(wishlist: Wishlist, selected: Boolean, onClick: () -> Unit) {
    Div(attrs = {
        classes("wishlist-card")
        if (selected) classes("wishlist-card-selected")
        onClick { onClick() }
    }) {
        Div(attrs = { classes("wishlist-art") }) {
            Div(attrs = { classes("wishlist-art-orbit") }) { Text("✦") }
            if (wishlist.gifts.isEmpty()) {
                Span(attrs = { classes("wishlist-art-empty") }) { Text("Добавьте первую идею") }
            } else {
                wishlist.gifts.take(4).forEachIndexed { index, gift ->
                    Span(attrs = { classes("gift-preview", "gift-preview-$index") }) {
                        Text(gift.title.take(1).uppercase())
                    }
                }
            }
        }
        Div(attrs = { classes("wishlist-card-body") }) {
            Div(attrs = { classes("wishlist-card-topline") }) {
                Span(attrs = { classes("visibility-pill") }) { Text(visibilityLabel(wishlist.baseVisibility)) }
                Span(attrs = { classes("chevron") }) { Text("↗") }
            }
            H3 { Text(wishlist.name) }
            wishlist.description?.takeIf { it.isNotBlank() }?.let {
                P(attrs = { classes("wishlist-card-description") }) { Text(it) }
            }
            Div(attrs = { classes("wishlist-card-meta") }) {
                Span { Text("${wishlist.gifts.size} ${giftWord(wishlist.gifts.size)}") }
                Span { Text("/${wishlist.slug}") }
            }
        }
    }
}

@Composable
private fun WishlistRow(wishlist: Wishlist, onClick: () -> Unit) {
    Div(attrs = { classes("list-row", "list-row-clickable"); onClick { onClick() } }) {
        Span(attrs = { classes("row-icon") }) { Text("✦") }
        Div {
            StrongText(wishlist.name)
            P(attrs = { classes("muted") }) { Text("${wishlist.gifts.size} ${giftWord(wishlist.gifts.size)}") }
        }
        Span(attrs = { classes("row-arrow") }) { Text("→") }
    }
}

@Composable
private fun WishlistDetails(wishlist: Wishlist, controller: WebController) {
    Section(attrs = { classes("panel", "details-panel") }) {
        Div(attrs = { classes("details-heading") }) {
            Div {
                SecondaryButton("← Назад", onClick = controller::closeWishlistDetails)
                Span(attrs = { classes("eyebrow") }) { Text("SELECTED LIST") }
                H2 { Text(wishlist.name) }
                P(attrs = { classes("muted") }) { Text("/${wishlist.slug} · ${visibilityLabel(wishlist.baseVisibility)}") }
            }
            Div(attrs = { classes("button-row", "compact") }) {
                SecondaryButton("Редактировать", onClick = { controller.showEditWishlist = true })
                SecondaryButton("Поделиться", onClick = { controller.createShareLink(wishlist.id) })
            }
        }
        wishlist.description?.takeIf { it.isNotBlank() }?.let { P { Text(it) } }
        controller.shareUrl?.let { url ->
            Div(attrs = { classes("share-box") }) {
                Span(attrs = { classes("eyebrow") }) { Text("SHARE LINK") }
                A(href = url, attrs = { classes("share-link"); attr("target", "_blank"); attr("rel", "noreferrer") }) {
                    Text(url)
                }
            }
        }
        if (controller.showEditWishlist) EditWishlistForm(wishlist, controller)
        if (wishlist.baseVisibility == BaseVisibility.SELECTED_PEOPLE) SelectedPeopleAccessPanel(wishlist, controller)
        Div(attrs = { classes("section-heading") }) {
            H3 { Text("Идеи подарков") }
            PrimaryButton("+ Добавить подарок", onClick = { controller.showCreateGift = true })
        }
        if (controller.showCreateGift) CreateGiftForm(wishlist.id, controller)
        if (wishlist.gifts.isEmpty()) {
            EmptyState("Список пока пуст", "Добавьте первую вещь, которую хотели бы получить.")
        } else {
            Div(attrs = { classes("gift-grid") }) { wishlist.gifts.forEach { GiftCard(it, controller, isOwner = true, ownerId = wishlist.ownerId) } }
        }
    }
}

@Composable
private fun SelectedPeopleAccessPanel(wishlist: Wishlist, controller: WebController) {
    var query by remember(wishlist.id) { mutableStateOf("") }
    Section(attrs = { classes("panel", "access-panel") }) {
        Span(attrs = { classes("eyebrow") }) { Text("SELECTED ACCESS") }
        H3 { Text("Кому открыть список") }
        P(attrs = { classes("muted") }) { Text("Найдите друга по username и добавьте его в список доступа.") }
        Div(attrs = { classes("friend-search-row") }) {
            TextField(query, { query = it }, "Username", placeholder = "например, alex")
            SecondaryButton("Найти", onClick = { controller.searchFriends(query) }, disabled = controller.isBusy || query.trim().length < 2)
        }
        controller.friendSearchResults.forEach { user ->
            Div(attrs = { classes("friend-row") }) {
                Div { StrongText(user.displayName); P(attrs = { classes("muted") }) { Text("@${user.username}") } }
                PrimaryButton("Открыть доступ", onClick = { controller.grantWishlistAccess(wishlist.id, user.id) }, disabled = controller.isBusy)
            }
        }
    }
}

@Composable
private fun CreateWishlistForm(controller: WebController) {
    var slug by remember { mutableStateOf("") }
    var name by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var visibility by remember { mutableStateOf(BaseVisibility.ONLY_ME) }
    var reservationVisibility by remember { mutableStateOf(ReservationVisibility.HIDDEN) }
    FormPanel("Новый wishlist", "Дайте списку понятное имя, чтобы к нему было легко вернуться.") {
        TextField(
            slug,
            { slug = it },
            "Короткая ссылка (необязательно)",
            placeholder = "birthday-2026",
            maxlength = 64,
        )
        if (slug.isNotBlank() && !isValidSlug(slug)) {
            P(attrs = { classes("field-error", "field-wide") }) { Text("Используйте 3–64 символа: латинские буквы, цифры и дефисы.") }
        }
        TextField(name, { name = it }, "Название", placeholder = "Идеи на день рождения")
        TextAreaField(description, { description = it }, "Описание", placeholder = "Что собрать в этом списке?")
        VisibilityPicker(visibility, onValueChange = { visibility = it })
        ReservationVisibilityPicker(reservationVisibility, onValueChange = { reservationVisibility = it })
        Div(attrs = { classes("button-row") }) {
            PrimaryButton(
                "Сохранить список",
                onClick = { controller.createWishlist(slug, name, description, visibility, reservationVisibility) },
                disabled = controller.isBusy || name.isBlank() || !isValidSlugOrEmpty(slug),
                loading = controller.isBusy,
            )
            SecondaryButton("Отмена", onClick = { controller.showCreateWishlist = false }, disabled = controller.isBusy)
        }
    }
}

@Composable
private fun EditWishlistForm(wishlist: Wishlist, controller: WebController) {
    var slug by remember(wishlist.id) { mutableStateOf(wishlist.slug) }
    var name by remember(wishlist.id) { mutableStateOf(wishlist.name) }
    var description by remember(wishlist.id) { mutableStateOf(wishlist.description.orEmpty()) }
    var visibility by remember(wishlist.id) { mutableStateOf(wishlist.baseVisibility) }
    var reservationVisibility by remember(wishlist.id) { mutableStateOf(wishlist.reservationVisibility) }
    FormPanel("Редактировать список", "Изменения применятся сразу.") {
        TextField(
            slug,
            { slug = it },
            "Короткая ссылка (необязательно)",
            maxlength = 64,
        )
        if (slug.isNotBlank() && !isValidSlug(slug)) {
            P(attrs = { classes("field-error", "field-wide") }) { Text("Используйте 3–64 символа: латинские буквы, цифры и дефисы.") }
        }
        TextField(name, { name = it }, "Название")
        TextAreaField(description, { description = it }, "Описание")
        VisibilityPicker(visibility, onValueChange = { visibility = it })
        ReservationVisibilityPicker(reservationVisibility, onValueChange = { reservationVisibility = it })
        Div(attrs = { classes("button-row") }) {
            PrimaryButton(
                "Сохранить изменения",
                onClick = { controller.updateWishlist(wishlist.id, slug, name, description, visibility, reservationVisibility) },
                disabled = controller.isBusy || name.isBlank() || !isValidSlugOrEmpty(slug),
                loading = controller.isBusy,
            )
            SecondaryButton("Отмена", onClick = { controller.showEditWishlist = false }, disabled = controller.isBusy)
        }
    }
}

@Composable
private fun VisibilityPicker(value: BaseVisibility, onValueChange: (BaseVisibility) -> Unit) {
    Div(attrs = { classes("visibility-picker") }) {
        Label { Text("Видимость списка") }
        P(attrs = { classes("muted") }) { Text("Кто сможет открыть этот wishlist.") }
        Div(attrs = { classes("visibility-options") }) {
            VisibilityOption(
                value = BaseVisibility.ONLY_ME,
                label = "Только я",
                description = "Список виден только вам.",
                selected = value,
                onValueChange = onValueChange,
            )
            VisibilityOption(
                value = BaseVisibility.FRIENDS,
                label = "Друзья",
                description = "Доступ только принятым друзьям.",
                selected = value,
                onValueChange = onValueChange,
            )
            VisibilityOption(
                value = BaseVisibility.EVERYONE,
                label = "Все",
                description = "Список доступен всем, у кого есть ссылка или доступ к вашему профилю.",
                selected = value,
                onValueChange = onValueChange,
            )
            VisibilityOption(
                value = BaseVisibility.SELECTED_PEOPLE,
                label = "Выбранные люди",
                description = "Доступ настраивается для конкретных друзей ниже.",
                selected = value,
                onValueChange = onValueChange,
            )
        }
    }
}

@Composable
private fun VisibilityOption(
    value: BaseVisibility,
    label: String,
    description: String,
    selected: BaseVisibility,
    onValueChange: (BaseVisibility) -> Unit,
    enabled: Boolean = true,
) {
    Button(attrs = {
        classes("visibility-option")
        if (value == selected) classes("visibility-option-selected")
        if (!enabled) attr("disabled", "true")
        onClick { if (enabled) onValueChange(value) }
    }) {
        Span(attrs = { classes("visibility-option-label") }) { Text(label) }
        Span(attrs = { classes("visibility-option-description") }) { Text(description) }
    }
}

@Composable
private fun ReservationVisibilityPicker(value: ReservationVisibility, onValueChange: (ReservationVisibility) -> Unit) {
    Div(attrs = { classes("visibility-picker") }) {
        Label { Text("Режим сюрприза") }
        P(attrs = { classes("muted") }) { Text("Выберите, какую информацию о выборе подарка увидит его создатель.") }
        Div(attrs = { classes("visibility-options") }) {
            VisibilityOption(
                value = BaseVisibility.ONLY_ME,
                label = "Никто не узнает",
                description = "Создатель списка не увидит, что подарок кто-то выбрал.",
                selected = if (value == ReservationVisibility.HIDDEN) BaseVisibility.ONLY_ME else BaseVisibility.FRIENDS,
                onValueChange = { onValueChange(ReservationVisibility.HIDDEN) },
            )
            VisibilityOption(
                value = BaseVisibility.FRIENDS,
                label = "Только статус",
                description = "Создатель увидит, что подарок выбран, но не узнает кем.",
                selected = if (value == ReservationVisibility.STATUS_ONLY) BaseVisibility.FRIENDS else BaseVisibility.ONLY_ME,
                onValueChange = { onValueChange(ReservationVisibility.STATUS_ONLY) },
            )
            VisibilityOption(
                value = BaseVisibility.SELECTED_PEOPLE,
                label = "Статус и имя",
                description = "Создатель увидит статус и имя выбравшего подарок.",
                selected = if (value == ReservationVisibility.FULL) BaseVisibility.SELECTED_PEOPLE else BaseVisibility.ONLY_ME,
                onValueChange = { onValueChange(ReservationVisibility.FULL) },
            )
        }
    }
}

@Composable
private fun CreateGiftForm(wishlistId: String, controller: WebController) {
    var title by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var productUrl by remember { mutableStateOf("") }
    var price by remember { mutableStateOf("") }
    var currency by remember { mutableStateOf("EUR") }
    FormPanel("Новая идея", "Цена необязательна — добавьте её, если это поможет.") {
        TextField(title, { title = it }, "Название", placeholder = "Наушники, книга, поездка…")
        TextAreaField(description, { description = it }, "Описание")
        Div(attrs = { classes("field-row") }) {
            TextField(productUrl, { productUrl = it }, "Ссылка на товар", placeholder = "https://example.com/product")
            SecondaryButton(
                "Заполнить по ссылке",
                onClick = {
                    controller.lookupProductMetadata(productUrl) { metadata ->
                        if (title.isBlank()) title = metadata.title.orEmpty()
                        if (description.isBlank()) description = metadata.description.orEmpty()
                        val metadataPrice = metadata.priceMinorUnits
                        if (price.isBlank() && metadataPrice != null) price = formatMinorUnits(metadataPrice, metadata.priceCurrency ?: currency).substringBeforeLast(' ')
                        metadata.priceCurrency?.let { currency = it }
                    }
                },
                disabled = controller.isBusy || productUrl.isBlank(),
            )
        }
        Div(attrs = { classes("field-row") }) {
            TextField(price, { price = it }, "Цена", placeholder = "29.90")
            TextField(currency, { currency = it }, "Валюта", placeholder = "EUR")
        }
        Div(attrs = { classes("button-row") }) {
            PrimaryButton(
                "Добавить идею",
                onClick = { controller.createGift(wishlistId, title, description, price, currency, productUrl) },
                disabled = controller.isBusy || title.isBlank(),
            )
            SecondaryButton("Отмена", onClick = { controller.showCreateGift = false }, disabled = controller.isBusy)
        }
    }
}

@Composable
private fun FormPanel(title: String, description: String, content: @Composable () -> Unit) {
    Section(attrs = { classes("panel", "form-panel") }) {
        H3 { Text(title) }
        P(attrs = { classes("muted") }) { Text(description) }
        Div(attrs = { classes("form-grid") }) { content() }
    }
}

@Composable
private fun GiftCard(gift: Gift, controller: WebController, isOwner: Boolean, ownerId: String) {
    LaunchedEffect(gift.id) { controller.loadCoordination(gift.id) }
    val reservation = controller.reservationFor(gift.id)
    val group = controller.groupFor(gift.id)
    var contribution by remember(gift.id) { mutableStateOf("") }
    var contributionCurrency by remember(gift.id) { mutableStateOf(gift.priceCurrency ?: "EUR") }
    Div(attrs = { classes("gift-card") }) {
        Div(attrs = { classes("gift-card-content-row") }) {
            if (gift.imageId != null) {
                Img(src = "/media/$ownerId/${gift.imageId}.png", alt = gift.title, attrs = { classes("gift-image") })
            } else {
                Span(attrs = { classes("gift-image", "gift-image-placeholder") }) { Text("✦") }
            }
            Div(attrs = { classes("gift-card-copy") }) {
                Div(attrs = { classes("gift-card-top") }) {
                    H3 { Text(gift.title) }
                    Span(attrs = { classes("gift-priority") }) { Text(gift.priority.name.lowercase()) }
                }
                gift.description?.takeIf { it.isNotBlank() }?.let { P(attrs = { classes("muted", "gift-description") }) { Text(it) } }
                if (gift.priceMinorUnits != null && gift.priceCurrency != null) {
                    val giftPrice = gift.priceMinorUnits
                    val giftCurrency = gift.priceCurrency
                    if (giftPrice != null && giftCurrency != null) StrongText(formatMinorUnits(giftPrice, giftCurrency))
                }
                gift.productUrl?.let { url ->
                    A(href = url, attrs = { classes("text-link"); attr("target", "_blank"); attr("rel", "noreferrer") }) { Text("Открыть ссылку →") }
                }
            }
        }
        if (controller.coordinationBusyGiftId == gift.id) {
            P(attrs = { classes("muted") }) { Text("Обновляем доступные действия…") }
        }
        if (!isOwner) {
            when (reservation?.status) {
                null -> PrimaryButton("Зарезервировать", onClick = { controller.reserveGift(gift.id) }, disabled = controller.coordinationBusyGiftId != null)
                ReservationStatus.ACTIVE -> {
                    SecondaryButton("Снять резерв", onClick = { controller.cancelReservation(gift.id) }, disabled = controller.coordinationBusyGiftId != null)
                    Div(attrs = { classes("button-row", "compact") }) {
                        SecondaryButton("Куплено", onClick = { controller.completeReservation(gift.id, ReservationStatus.PURCHASED) }, disabled = controller.coordinationBusyGiftId != null)
                        SecondaryButton("Подарено", onClick = { controller.completeReservation(gift.id, ReservationStatus.GIFTED) }, disabled = controller.coordinationBusyGiftId != null)
                    }
                }
                ReservationStatus.CANCELLED -> PrimaryButton("Зарезервировать снова", onClick = { controller.reserveGift(gift.id) }, disabled = controller.coordinationBusyGiftId != null)
                ReservationStatus.PURCHASED -> P(attrs = { classes("muted") }) { Text("Уже куплено") }
                ReservationStatus.GIFTED -> P(attrs = { classes("muted") }) { Text("Уже подарено") }
            }
        }
        if (!isOwner && group?.enabled == true) {
            val groupCurrency = group.currency ?: contributionCurrency
            val total = formatMinorUnits(group.totalPledgedMinorUnits, groupCurrency)
            val target = group.targetMinorUnits?.let { formatMinorUnits(it, groupCurrency) } ?: "цель не задана"
            P(attrs = { classes("muted") }) { Text("Подарок можно выбрать вместе с другими.") }
            P(attrs = { classes("muted") }) { Text("Собрано: $total из $target") }
            Div(attrs = { classes("button-row", "compact") }) {
                TextField(contribution, { contribution = it }, "Ваш вклад", placeholder = "25.00")
                TextField(contributionCurrency, { contributionCurrency = it }, "Валюта", placeholder = "EUR")
                SecondaryButton(
                    "Добавить вклад",
                    onClick = { controller.addContribution(gift.id, contribution, contributionCurrency) },
                    disabled = controller.coordinationBusyGiftId != null || contribution.isBlank(),
                )
            }
        }
    }
}

@Composable
private fun FriendsView(state: WebAppState.SignedIn, controller: WebController) {
    var query by remember { mutableStateOf("") }
    val incoming = state.friends.filter { it.status == FriendshipStatus.PENDING && it.isIncoming }
    val outgoing = state.friends.filter { it.status == FriendshipStatus.PENDING && !it.isIncoming }
    val accepted = state.friends.filter { it.status == FriendshipStatus.ACCEPTED }

    Div(attrs = { classes("page-heading") }) {
        Div {
            Span(attrs = { classes("eyebrow") }) { Text("YOUR CIRCLE") }
            H1 { Text("Друзья") }
            P(attrs = { classes("muted") }) {
                Text("Находите знакомых, отправляйте запросы и делитесь списками только с нужными людьми.")
            }
        }
        SecondaryButton("Обновить", controller::refresh, disabled = controller.isBusy)
    }

    controller.viewedUser?.let { user ->
        ViewedWishlistsPanel(user, controller.viewedUserWishlists, controller)
    }

    Section(attrs = { classes("panel", "friends-search") }) {
        Span(attrs = { classes("eyebrow") }) { Text("DISCOVER PEOPLE") }
        H2 { Text("Найти друзей") }
        P(attrs = { classes("muted") }) { Text("Поиск работает по username и отображаемому имени.") }
        Div(attrs = { classes("friend-search-row") }) {
            TextField(query, { query = it }, "Поиск", placeholder = "например, alex")
            PrimaryButton(
                "Найти",
                onClick = { controller.searchFriends(query) },
                disabled = controller.isBusy || query.trim().length < 2,
            )
        }
        if (controller.friendSearchResults.isNotEmpty()) {
            Div(attrs = { classes("friends-results") }) {
                controller.friendSearchResults.forEach { user -> FriendSearchCard(user, state, controller) }
            }
        }
    }

    if (incoming.isNotEmpty()) {
        Section(attrs = { classes("panel", "friends-panel") }) {
            Div(attrs = { classes("panel-heading") }) {
                Div {
                    Span(attrs = { classes("eyebrow") }) { Text("WAITING FOR YOU") }
                    H2 { Text("Запросы в друзья") }
                }
                Span(attrs = { classes("count-badge") }) { Text("${incoming.size}") }
            }
            incoming.forEach { friendship -> FriendshipRow(friendship, controller) }
        }
    }

    Section(attrs = { classes("panel", "friends-panel") }) {
        Div(attrs = { classes("panel-heading") }) {
            Div {
                Span(attrs = { classes("eyebrow") }) { Text("YOUR PEOPLE") }
                H2 { Text("Мои друзья") }
            }
            Span(attrs = { classes("count-badge") }) { Text("${accepted.size}") }
        }
        when {
            accepted.isNotEmpty() -> accepted.forEach { FriendshipRow(it, controller) }
            outgoing.isNotEmpty() -> EmptyState("Пока нет принятых запросов", "Ваши исходящие запросы отображаются ниже.")
            else -> EmptyState("Здесь пока никого нет", "Найдите первого друга по username и отправьте запрос.")
        }
        if (outgoing.isNotEmpty()) {
            Div(attrs = { classes("outgoing-heading") }) {
                Span(attrs = { classes("eyebrow") }) { Text("OUTGOING") }
                StrongText("Ожидают ответа")
            }
            outgoing.forEach { FriendshipRow(it, controller) }
        }
    }
}

@Composable
private fun FriendSearchCard(user: UserCard, state: WebAppState.SignedIn, controller: WebController) {
    val existing = state.friends.firstOrNull { it.user.id == user.id }
    val canOpenWishlists = existing?.status == FriendshipStatus.ACCEPTED
    Div(attrs = {
        classes("friend-row")
        if (canOpenWishlists) {
            classes("friend-row-clickable")
            onClick { controller.openUserWishlists(user) }
        }
    }) {
        Span(attrs = { classes("avatar") }) { Text(user.displayName.take(1).uppercase()) }
        Div {
            StrongText(user.displayName)
            P(attrs = { classes("muted") }) { Text("@${user.username}") }
        }
        Div(attrs = { classes("friend-actions") }) {
            when {
                existing == null -> PrimaryButton(
                    "Добавить",
                    onClick = { controller.sendFriendRequest(user.id) },
                    disabled = controller.isBusy,
                )
                existing.status == FriendshipStatus.ACCEPTED -> FriendStatus("Уже друзья", "friend-status-accepted")
                existing.status == FriendshipStatus.PENDING && existing.isIncoming -> FriendStatus("Есть входящий запрос", "friend-status-pending")
                existing.status == FriendshipStatus.PENDING -> FriendStatus("Запрос отправлен", "friend-status-pending")
                else -> FriendStatus("Запрос уже обработан", "friend-status-muted")
            }
        }
    }
}

@Composable
private fun FriendshipRow(friendship: Friendship, controller: WebController) {
    Div(attrs = {
        classes("friend-row")
        if (friendship.status == FriendshipStatus.ACCEPTED) {
            classes("friend-row-clickable")
            onClick { controller.openUserWishlists(friendship.user) }
        }
    }) {
        Span(attrs = { classes("avatar") }) { Text(friendship.user.displayName.take(1).uppercase()) }
        Div {
            StrongText(friendship.user.displayName)
            P(attrs = { classes("muted") }) { Text("@${friendship.user.username}") }
        }
        Div(attrs = { classes("friend-actions") }) {
            if (friendship.status == FriendshipStatus.PENDING && friendship.isIncoming) {
                PrimaryButton(
                    "Принять",
                    onClick = { controller.respondToFriendRequest(friendship.id, FriendshipStatus.ACCEPTED) },
                    disabled = controller.isBusy,
                )
                SecondaryButton(
                    "Отклонить",
                    onClick = { controller.respondToFriendRequest(friendship.id, FriendshipStatus.DECLINED) },
                    disabled = controller.isBusy,
                )
            } else {
                val (label, cssClass) = when (friendship.status) {
                    FriendshipStatus.ACCEPTED -> "Друг" to "friend-status-accepted"
                    FriendshipStatus.PENDING -> "Ожидает ответа" to "friend-status-pending"
                    FriendshipStatus.DECLINED -> "Отклонён" to "friend-status-muted"
                    FriendshipStatus.BLOCKED -> "Заблокирован" to "friend-status-muted"
                }
                FriendStatus(label, cssClass)
            }
        }
    }
}

@Composable
private fun FriendStatus(label: String, cssClass: String) {
    Span(attrs = { classes("friend-status", cssClass) }) { Text(label) }
}

@Composable
private fun ViewedWishlistsPanel(
    user: UserCard,
    wishlists: List<Wishlist>,
    controller: WebController,
) {
    Section(attrs = { classes("panel", "external-wishlists-panel") }) {
        Div(attrs = { classes("panel-heading") }) {
            Div {
                Span(attrs = { classes("eyebrow") }) { Text("SHARED WITH YOU") }
                H2 { Text("Открытые желания ${user.displayName}") }
                P(attrs = { classes("muted") }) { Text("@${user.username} · только доступные вам подборки") }
            }
            SecondaryButton("Закрыть", controller::closeUserWishlists, disabled = controller.isBusy)
        }
        if (wishlists.isEmpty()) {
            EmptyState("Нет доступных списков", "Пользователь пока не открыл списки для вас.")
        } else {
            Div(attrs = { classes("external-wishlist-grid") }) {
                wishlists.forEach { wishlist -> ExternalWishlistCard(wishlist, controller) }
            }
        }
    }
}

@Composable
private fun ExternalWishlistCard(wishlist: Wishlist, controller: WebController) {
    Div(attrs = { classes("external-wishlist-card") }) {
        Span(attrs = { classes("eyebrow") }) { Text(visibilityLabel(wishlist.baseVisibility)) }
        H3 { Text(wishlist.name) }
        wishlist.description?.takeIf { it.isNotBlank() }?.let { P(attrs = { classes("muted") }) { Text(it) } }
        P(attrs = { classes("muted") }) { Text("${wishlist.gifts.size} ${giftWord(wishlist.gifts.size)}") }
        if (wishlist.gifts.isNotEmpty()) {
            Div(attrs = { classes("gift-grid") }) {
                wishlist.gifts.forEach { gift -> GiftCard(gift, controller, isOwner = false, ownerId = wishlist.ownerId) }
            }
        }
    }
}

@Composable
private fun BirthdaysView(state: WebAppState.SignedIn) {
    val today = remember { Date() }
    var month by remember { mutableStateOf(today.getMonth()) }
    var year by remember { mutableStateOf(today.getFullYear()) }
    var selectedDay by remember { mutableStateOf(today.getDate()) }
    val monthNames = listOf("Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь")
    val daysInMonth = Date(year, month + 1, 0).getDate()
    val firstDayOffset = (Date(year, month, 1).getDay() + 6) % 7
    val selected = state.birthdays.filter { it.birthday.month == month + 1 && it.birthday.day == selectedDay }
    Div(attrs = { classes("page-heading") }) {
        Div {
            Span(attrs = { classes("eyebrow") }) { Text("НЕ ЗАБЫТЬ") }
            H1 { Text("Календарь") }
        }
    }
    Div(attrs = { classes("calendar-layout") }) {
        Section(attrs = { classes("calendar-card") }) {
            Div(attrs = { classes("calendar-toolbar") }) {
                Button(attrs = { classes("text-button"); onClick {
                    if (month == 0) { month = 11; year -= 1 } else month -= 1
                    selectedDay = 1
                } }) { Text("←") }
                H2 { Text("${monthNames[month]} $year") }
                Button(attrs = { classes("text-button"); onClick {
                    if (month == 11) { month = 0; year += 1 } else month += 1
                    selectedDay = 1
                } }) { Text("→") }
            }
            Div(attrs = { classes("calendar-grid") }) {
                listOf("ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС").forEach { label ->
                    Span(attrs = { classes("calendar-weekday") }) { Text(label) }
                }
                repeat(firstDayOffset) { Span(attrs = { classes("calendar-day-empty") }) }
                (1..daysInMonth).forEach { day ->
                    val hasEvent = state.birthdays.any { it.birthday.month == month + 1 && it.birthday.day == day }
                    Button(attrs = {
                        classes("calendar-day")
                        if (hasEvent) classes("calendar-day-has-event")
                        if (day == selectedDay) classes("calendar-day-selected")
                        onClick { selectedDay = day }
                    }) { Text(day.toString()) }
                }
            }
        }
        Section(attrs = { classes("calendar-card", "birthdays-panel") }) {
            Span(attrs = { classes("eyebrow") }) { Text("ВЫБРАННЫЙ ДЕНЬ") }
            H2 { Text("$selectedDay ${monthNames[month].lowercase()}") }
            if (selected.isEmpty()) {
                EmptyState("В этот день нет событий", "Можно спокойно планировать свои дела.")
            } else selected.forEach { birthday ->
                Div(attrs = { classes("birthday-row") }) {
                    Span(attrs = { classes("avatar") }) { Text(birthday.user.displayName.take(1).uppercase()) }
                    Div {
                        StrongText(birthday.user.displayName)
                        P(attrs = { classes("muted") }) { Text("@${birthday.user.username}") }
                    }
                }
            }
            if (selected.isEmpty() && state.birthdays.isNotEmpty()) {
                H3 { Text("Ближайшие") }
                state.birthdays.take(4).forEach { birthday ->
                    Div(attrs = { classes("birthday-row") }) {
                        Span(attrs = { classes("avatar") }) { Text(birthday.user.displayName.take(1).uppercase()) }
                        Div { StrongText(birthday.user.displayName); P(attrs = { classes("muted") }) { Text(formatBirthday(birthday.birthday)) } }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProfileView(state: WebAppState.SignedIn, controller: WebController) {
    val user = state.user
    var editing by remember(user.id) { mutableStateOf(false) }
    var displayName by remember(user.id) { mutableStateOf(user.displayName) }
    var bio by remember(user.id) { mutableStateOf(user.bio.orEmpty()) }
    var currency by remember(user.id) { mutableStateOf(user.preferredCurrency) }
    var birthdayInput by remember(user.id) { mutableStateOf(user.birthday?.toDateInputValue().orEmpty()) }
    var birthdayYearKnown by remember(user.id) { mutableStateOf(user.birthday?.year != null) }
    var showBirthYear by remember(user.id) { mutableStateOf(user.showBirthYear) }
    Div(attrs = { classes("page-heading") }) {
        Div {
            H1 { Text("Профиль") }
        }
        Div(attrs = { classes("page-heading-actions") }) {
            if (editing) SecondaryButton("Отмена", onClick = { editing = false })
            else PrimaryButton("Редактировать профиль", onClick = { editing = true })
        }
    }
    if (!editing) {
        Section(attrs = { classes("profile-identity") }) {
            Span(attrs = { classes("profile-avatar") }) { Text(user.displayName.take(1).uppercase()) }
            Div {
                H2 { Text(user.displayName) }
                P(attrs = { classes("muted") }) { Text("@${user.username}") }
                Span(attrs = { classes("friend-status", "friend-status-accepted") }) { Text("Профиль заполнен") }
            }
        }
        Div(attrs = { classes("profile-columns") }) {
            Section(attrs = { classes("panel") }) {
                Span(attrs = { classes("eyebrow") }) { Text("О СЕБЕ") }
                H2 { Text("Немного личного") }
                P(attrs = { classes("muted") }) { Text(user.bio?.takeIf { it.isNotBlank() } ?: "Расскажите друзьям о своих интересах.") }
                Div(attrs = { classes("button-row") }) {
                    Span(attrs = { classes("visibility-pill") }) { Text(user.preferredCurrency) }
                    user.birthday?.let { Span(attrs = { classes("visibility-pill") }) { Text(formatBirthday(it)) } }
                }
            }
            Section(attrs = { classes("panel") }) {
                Div(attrs = { classes("panel-heading") }) {
                    Div { Span(attrs = { classes("eyebrow") }) { Text("ВАШИ СПИСКИ") }; H2 { Text("Вишлисты") } }
                    Span(attrs = { classes("count-badge") }) { Text(state.wishlists.size.toString()) }
                }
                if (state.wishlists.isEmpty()) EmptyState("Пока нет списков", "Создайте первый вишлист.")
                else state.wishlists.take(4).forEach { wishlist ->
                    WishlistRow(wishlist) { controller.selectedTab = WebTab.WISHLISTS; controller.openWishlistDetails(wishlist.id) }
                }
            }
        }
    } else {
    Section(attrs = { classes("panel", "profile-panel") }) {
        Span(attrs = { classes("eyebrow") }) { Text("РЕДАКТИРОВАНИЕ") }
        H2 { Text("Данные профиля") }
        Div(attrs = { classes("form-grid", "profile-form") }) {
            TextField(displayName, { displayName = it }, "Имя")
            TextField(currency, { currency = it }, "Валюта", placeholder = "EUR")
            TextAreaField(bio, { bio = it }, "О себе", placeholder = "Пара слов о ваших интересах")
            Div(attrs = { classes("profile-birthday", "field-wide") }) {
                H3 { Text("День рождения") }
                P(attrs = { classes("muted") }) {
                    Text("Выберите дату в нативном календаре браузера. Год можно скрыть от друзей.")
                }
                TextField(
                    birthdayInput,
                    { birthdayInput = it; birthdayYearKnown = it.isNotBlank() },
                    "Дата рождения",
                    InputType.Date,
                    name = "birthday",
                )
                Div(attrs = { classes("birthday-visibility") }) {
                    SecondaryButton(
                        if (showBirthYear) "Год виден друзьям" else "Год скрыт от друзей",
                        onClick = { showBirthYear = !showBirthYear },
                    )
                    P(attrs = { classes("muted") }) {
                        Text(if (showBirthYear) "Друзья увидят полный год рождения." else "Друзья увидят только день и месяц.")
                    }
                }
            }
            PrimaryButton(
                "Сохранить профиль",
                onClick = {
                    controller.saveProfile(
                        displayName,
                        bio,
                        currency,
                        birthdayInput,
                        birthdayYearKnown,
                        showBirthYear,
                    )
                    editing = false
                },
                disabled = controller.isBusy || displayName.isBlank() || currency.isBlank(),
                loading = controller.isBusy,
            )
        }
    }
    }
}

private enum class SettingsSection { ROOT, PASSWORD, PRIVACY, CURRENCY }

@Composable
private fun SettingsView(state: WebAppState.SignedIn, controller: WebController) {
    val user = state.user
    var section by remember { mutableStateOf(SettingsSection.ROOT) }
    var currentPassword by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var currency by remember(user.id) { mutableStateOf(user.preferredCurrency) }
    var searchable by remember { mutableStateOf(true) }
    var contactDiscovery by remember { mutableStateOf(false) }
    var reminders by remember { mutableStateOf(true) }

    Div(attrs = { classes("page-heading") }) {
        Div {
            if (section != SettingsSection.ROOT) Button(attrs = { classes("text-button"); onClick { section = SettingsSection.ROOT } }) { Text("← Назад") }
            H1 { Text(when (section) {
                SettingsSection.ROOT -> "Настройки"
                SettingsSection.PASSWORD -> "Пароль и безопасность"
                SettingsSection.PRIVACY -> "Приватность"
                SettingsSection.CURRENCY -> "Валюта"
            }) }
        }
    }
    when (section) {
        SettingsSection.ROOT -> Div(attrs = { classes("settings-layout") }) {
            Section(attrs = { classes("settings-list") }) {
                SettingsRow("✎", "Редактировать профиль", "Имя, описание и день рождения") { controller.selectedTab = WebTab.PROFILE }
                SettingsRow("@", "Email", user.email, action = null)
                SettingsRow("●", "Пароль и безопасность", "Изменить пароль аккаунта") { section = SettingsSection.PASSWORD }
                SettingsRow("◌", "Приватность", "Поиск профиля, контакты и напоминания") { section = SettingsSection.PRIVACY }
                SettingsRow("¤", "Валюта по умолчанию", user.preferredCurrency) { section = SettingsSection.CURRENCY }
            }
            Section(attrs = { classes("panel", "accent-panel") }) {
                Span(attrs = { classes("eyebrow") }) { Text("СЕССИЯ") }
                H2 { Text("Аккаунт") }
                P(attrs = { classes("muted") }) { Text("Выйдите на этом устройстве или удалите аккаунт со всеми данными.") }
                Div(attrs = { classes("button-row") }) {
                    SecondaryButton("Выйти", controller::logout, disabled = controller.isBusy)
                    SecondaryButton("Удалить аккаунт", controller::deleteAccount, disabled = controller.isBusy)
                }
            }
        }
        SettingsSection.PASSWORD -> Section(attrs = { classes("panel", "profile-panel") }) {
            P(attrs = { classes("muted") }) { Text("Новый пароль должен содержать не менее 8 символов.") }
            Div(attrs = { classes("form-grid") }) {
                TextField(currentPassword, { currentPassword = it }, "Текущий пароль", InputType.Password, autocomplete = "current-password")
                TextField(newPassword, { newPassword = it }, "Новый пароль", InputType.Password, autocomplete = "new-password")
                P { Text("8–256 символов, минимум одна буква и одна цифра.") }
                TextField(confirmPassword, { confirmPassword = it }, "Повторите новый пароль", InputType.Password, autocomplete = "new-password")
                PrimaryButton(
                    "Изменить пароль",
                    onClick = { controller.changePassword(currentPassword, newPassword) { section = SettingsSection.ROOT } },
                    disabled = controller.isBusy || currentPassword.isBlank() || UserInputRules.validatePassword(newPassword) != null || newPassword != confirmPassword,
                    loading = controller.isBusy,
                )
            }
        }
        SettingsSection.CURRENCY -> Section(attrs = { classes("panel", "profile-panel") }) {
            P(attrs = { classes("muted") }) { Text("Эта валюта будет подставляться при добавлении новых подарков.") }
            Div(attrs = { classes("form-grid") }) {
                TextField(currency, { currency = it.uppercase().take(3) }, "Валюта", placeholder = "EUR")
                PrimaryButton("Сохранить", onClick = {
                    controller.saveProfile(user.displayName, user.bio.orEmpty(), currency, user.birthday?.toDateInputValue().orEmpty(), user.birthday?.year != null, user.showBirthYear)
                    section = SettingsSection.ROOT
                }, disabled = controller.isBusy || currency.length != 3, loading = controller.isBusy)
            }
        }
        SettingsSection.PRIVACY -> Section(attrs = { classes("settings-list", "profile-panel") }) {
            SettingsToggle("Показывать профиль в поиске", "Люди смогут найти вас по имени и username.", searchable) { searchable = it }
            SettingsToggle("Поиск по контактам", "Разрешить находить профиль по безопасно обработанным контактам.", contactDiscovery) { contactDiscovery = it }
            SettingsToggle("Напоминания о днях рождения", "Показывать ближайшие даты друзей.", reminders) { reminders = it }
            Div(attrs = { classes("button-row") }) {
                PrimaryButton("Сохранить настройки", onClick = {
                    val storage: dynamic = js("window.localStorage")
                    storage.setItem("presentocracy.searchable", searchable.toString())
                    storage.setItem("presentocracy.contacts", contactDiscovery.toString())
                    storage.setItem("presentocracy.reminders", reminders.toString())
                    section = SettingsSection.ROOT
                })
            }
        }
    }
}

@Composable
private fun SettingsRow(icon: String, title: String, description: String, action: (() -> Unit)?) {
    Div(attrs = { classes("settings-row") }) {
        Span(attrs = { classes("settings-icon") }) { Text(icon) }
        Div(attrs = { classes("settings-copy") }) { StrongText(title); P { Text(description) } }
        if (action != null) Button(attrs = { classes("settings-action"); onClick { action() } }) { Text("›") }
    }
}

@Composable
private fun SettingsToggle(title: String, description: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Div(attrs = { classes("settings-row") }) {
        Div(attrs = { classes("settings-copy") }) { StrongText(title); P { Text(description) } }
        Button(attrs = {
            classes("settings-toggle")
            if (checked) classes("settings-toggle-on")
            attr("role", "switch")
            attr("aria-checked", checked.toString())
            onClick { onChange(!checked) }
        }) { Span {} }
    }
}

@Composable
private fun TextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    type: InputType<String> = InputType.Text,
    placeholder: String = "",
    autocomplete: String? = null,
    name: String? = null,
    maxlength: Int? = null,
) {
    Div(attrs = { classes("field") }) {
        Label { Text(label) }
        Input(type = type, attrs = {
            classes("input")
            value(value)
            if (placeholder.isNotEmpty()) placeholder(placeholder)
            autocomplete?.let { attr("autocomplete", it) }
            name?.let { attr("name", it) }
            maxlength?.let { attr("maxlength", it.toString()) }
            onInput { onValueChange(it.value) }
        })
    }
}

@Composable
private fun TextAreaField(value: String, onValueChange: (String) -> Unit, label: String, placeholder: String = "") {
    Div(attrs = { classes("field", "field-wide") }) {
        Label { Text(label) }
        TextArea(attrs = {
            classes("input", "textarea")
            value(value)
            if (placeholder.isNotEmpty()) placeholder(placeholder)
            onInput { onValueChange(it.value) }
        })
    }
}

@Composable
private fun PrimaryButton(
    label: String,
    onClick: () -> Unit,
    disabled: Boolean = false,
    loading: Boolean = false,
) {
    Button(attrs = {
        classes("button", "button-primary")
        if (disabled) attr("disabled", "true")
        onClick { onClick() }
    }) {
        if (loading) Span(attrs = { classes("button-spinner") })
        Text(label)
    }
}

@Composable
private fun SecondaryButton(label: String, onClick: () -> Unit, disabled: Boolean = false) {
    Button(attrs = {
        classes("button", "button-secondary")
        if (disabled) attr("disabled", "true")
        onClick { onClick() }
    }) { Text(label) }
}

@Composable
private fun MessageBanner(message: String, error: Boolean) {
    Div(attrs = {
        classes("message-banner")
        if (error) classes("message-error")
    }) { Text(message) }
}

@Composable
private fun EmptyState(title: String, description: String) {
    Div(attrs = { classes("empty-state") }) {
        Span(attrs = { classes("empty-icon") }) { Text("✦") }
        StrongText(title)
        P(attrs = { classes("muted") }) { Text(description) }
    }
}

@Composable
private fun CenteredMessage(text: String) {
    Div(attrs = { classes("centered-message") }) { Text(text) }
}

@Composable
private fun StrongText(text: String) {
    Span(attrs = { classes("strong-text") }) { Text(text) }
}

private fun giftWord(count: Int): String = when {
    count % 10 == 1 && count % 100 != 11 -> "идея"
    count % 10 in 2..4 && count % 100 !in 12..14 -> "идеи"
    else -> "идей"
}

private fun wishlistWord(count: Int): String = when {
    count % 10 == 1 && count % 100 != 11 -> "список"
    count % 10 in 2..4 && count % 100 !in 12..14 -> "списка"
    else -> "списков"
}

private fun formatMinorUnits(minorUnits: Long, currency: String): String {
    val major = minorUnits / 100
    val minor = (minorUnits % 100).toString().padStart(2, '0')
    return "$major.${minor} ${currency.uppercase()}"
}

private fun formatBirthday(birthday: Birthday): String = buildString {
    append(birthday.day.toString().padStart(2, '0'))
    append('.')
    append(birthday.month.toString().padStart(2, '0'))
    birthday.year?.let {
        append('.')
        append(it)
    }
}

private fun Birthday.toDateInputValue(): String =
    "${(year ?: 2000).toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"

private fun isValidSlug(value: String): Boolean = value.trim().matches(Regex("[A-Za-z0-9-]{3,64}"))

private fun isValidSlugOrEmpty(value: String): Boolean = value.trim().isEmpty() || isValidSlug(value)

private fun visibilityLabel(value: BaseVisibility): String = when (value) {
    BaseVisibility.ONLY_ME -> "только я"
    BaseVisibility.FRIENDS -> "друзья"
    BaseVisibility.SELECTED_PEOPLE -> "выбранные люди"
    BaseVisibility.EVERYONE, BaseVisibility.PUBLIC -> "все"
}

private fun isDesignReviewRequested(): Boolean =
    js("new URLSearchParams(window.location.search).get('review') === '1'") as Boolean
